import { useEffect, useMemo, useRef } from "react";
import { useEffectOnce } from "usehooks-ts";
import { Select, theme } from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { columnForAggregation } from "@vzbl/integrations";
import { asKey } from "src/utils/fields";
import { Flex } from "src/components/Flex";
import { cloneData } from "src/utils/objects";
import { SearchState, SearchWindow } from "src/search/types";
import { DynamicCanvas } from "src/components/DynamicCanvas";
import { useSubscribeEffect } from "src/hooks/useSubscribeEffect";
import { labelSize } from "src/utils/labels";
import { floorPow2 } from "src/utils/numbers";
import { ExploreContext } from "../useExploreContext";
import { InvalidGroupBys } from "./InvalidGroupBys";
import { VisualizationProps } from "./visualization-props";
import { valueToNumber } from "./common";
import { formatValue, unitTypeForCol } from "src/utils/strings";
import {
  CompareWithConfig,
  CompareWithControls,
  isChangeColor,
  toCompareWithConfig,
  useChangeColor,
} from "./CompareWithControls";
import { FilterMenu } from "src/search/components/inputs/FilterMenu";
import { usePopupContext } from "src/hooks/usePopup";
import { FormattingControls, hasTimeUnit } from "./FormattingControls";
import { FormattingConfig, toFormattingConfig } from "./FormattingControls";
import { TimeUnit } from "./FormattingControls";
import { ValueFormatter, ValueType } from "src/utils/formatters";

type Config = CompareWithConfig &
  FormattingConfig & {
    sort: "ascending" | "descending";
    size: "large" | "medium" | "small";
    column: string | undefined;
  };

const toConfig: (data: Config | any) => Config = (data) => ({
  ...toCompareWithConfig(data),
  ...toFormattingConfig(data, { timeUnit: TimeUnit.AUTO }),
  // left for backwards compatibility...
  ...(data && isChangeColor(data.color) ? { changeColor: data.color } : {}),
  sort: ["ascending"].includes(data?.sort) ? data.sort : "descending",
  size: ["medium", "small"].includes(data?.size) ? data?.size : "large",
  column: typeof data?.column === "string" ? data.column : undefined,
});

const useConfig = (data: Config | any) => useMemo(() => toConfig(data), [data]);

export function Metric({ context }: VisualizationProps) {
  useEffectOnce(() => {
    context.view.setConfig(toConfig(context.view.config));
  });
  const config = useConfig(context.view.config);

  if (!context.params.state.groupBys?.length) {
    return (
      <InvalidGroupBys
        numGroups={context.params.selectors.countGroupBys(context.params.state)}
        view={context.view}
        requiredGroups={{ min: 1, max: 1 }}
      />
    );
  }
  return <Ready {...config} context={context} />;
}

function Ready({
  context,
  ...config
}: Config & Pick<VisualizationProps, "context">) {
  const window: SearchWindow = {
    limit: 1,
    order: [
      { by: context.params.state.aggregations?.at(0)!, direction: config.sort },
    ],
  };
  const { token } = theme.useToken();
  const {
    ready,
    label,
    entityUuid,
    subquery: _VZBL_1135,
    compareValues,
    unit,
    value,
  } = useMetric({
    context,
    window,
    showChangeAs: config.showChangeAs,
    column: config.column,
  });
  const baseFontSize = 12;
  const size = useMemo(() => labelSize([label], baseFontSize), [label]);
  const color = useChangeColor(config.changeColor, label);
  const direction = label.startsWith("-") ? (
    <ArrowDownOutlined />
  ) : (
    <ArrowUpOutlined />
  );

  const comparing = config.showChangeValues && Array.isArray(compareValues);
  const fontSizeRounder =
    config.size === "small"
      ? (n: number) => Math.min(20, Math.max(14, Math.floor(n / 2) * 2))
      : config.size === "medium"
      ? (n: number) => Math.min(32, Math.max(18, Math.floor(n / 4) * 4))
      : (n: number) => Math.min(64, Math.max(24, Math.floor(n / 8) * 8));
  const popup = usePopupContext();
  return (
    <DynamicCanvas>
      {({ height, width }) => {
        const fontSize = (() => {
          const ratio = Math.max((width / Math.max(size, 1)) * baseFontSize, 1);
          const scale = Math.min(
            1,
            (height - (unit ? 24 : 0) - (comparing ? 16 : 0)) / ratio
          );
          const fontSize = ratio * scale * 0.8;
          return fontSizeRounder(fontSize);
        })();
        return (
          <Flex style={{ height: "100%", width: "100%", overflow: "hidden" }}>
            {ready ? (
              <Flex
                direction="column"
                style={{ width: "100%" }}
                onClick={(e) => {
                  if (entityUuid) {
                    context.entity.open({ uuid: entityUuid });
                  } else {
                    popup.setPopup(
                      e,
                      <FilterMenu
                        context={context}
                        values={[]}
                        properties={[]}
                        options={{
                          showDrillDown: true,
                          showEntityOpener: true,
                        }}
                      />
                    );
                  }
                }}
              >
                <div
                  className={`filter-tag-hover`}
                  style={{
                    fontSize,
                    lineHeight: `${fontSize}px`,
                  }}
                >
                  {comparing ? (
                    compareValues[1]
                  ) : (
                    <span style={{ color }}>
                      {label} {unit}
                    </span>
                  )}
                </div>

                {comparing && (
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 8,
                      color: token.colorTextSecondary,
                      fontSize: 13,
                      lineHeight: "16px",
                    }}
                  >
                    <span
                      style={{
                        minWidth: 0,
                        maxWidth: "100%",
                        textAlign: "right",
                      }}
                      className="trunc"
                    >
                      <span style={{ color }}>
                        {direction} {label} {unit}
                      </span>
                    </span>
                    <span
                      style={{
                        minWidth: 0,
                        maxWidth: "100%",
                        textAlign: "left",
                      }}
                      className="trunc"
                    >
                      ({compareValues[0]})
                    </span>
                  </div>
                )}
              </Flex>
            ) : (
              <LoadingOutlined />
            )}
          </Flex>
        );
      }}
    </DynamicCanvas>
  );
}

function useMetric({
  context,
  window,
  showChangeAs,
  column,
}: { window: SearchWindow } & Pick<VisualizationProps, "context"> &
  Pick<Config, "showChangeAs" | "column">): {
  value: number;
  compareValues?: [string, string];
  label: string;
  ready: boolean;
  entityUuid?: string;
  unit?: string;
  subquery?: SearchState;
} {
  useSubscribeEffect(context.aggregations);
  useSubscribeEffect(context.entitiesList);

  const windowSetter = useRef({
    fn(window: SearchWindow) {
      context.aggregations.setWindow(window);
      context.entitiesList.setWindow(window);
    },
  });
  const windowValue = useRef(window);
  windowValue.current = window;
  const windowCheckpoint = JSON.stringify(window);
  useEffect(() => {
    const setWindow = windowSetter.current.fn;
    setWindow(windowValue.current);
  }, [windowCheckpoint]);

  const hasGroupsBys = context.params.selectors.hasGroupBys(
    context.params.state
  );
  const ready = hasGroupsBys
    ? context.aggregations.isFetched
    : context.entitiesList.isFetched;

  const columnInfo = hasGroupsBys
    ? context.aggregations.data?.meta?.columnInfo || []
    : context.entitiesList.data?.meta?.columnInfo || [];

  const measureColumn =
    column &&
    (context.params.state.aggregations?.some((agg) => asKey(agg) === column) ||
      context.params.state.formulas?.some((formula) => formula.name === column))
      ? column
      : context.params.selectors.getColumnForAggregation(
          context.params.state,
          context.params.state.aggregations?.at(0)
        );

  const { type, unit, tuple } = unitTypeForCol(measureColumn, columnInfo);
  const precision =
    context.view.config.timeUnit === TimeUnit.AUTO
      ? 2
      : context.view.config.timeUnit;
  const [groupedData, subquery] = useMemo(() => {
    const rows = context.aggregations.data?.data || [];
    const groupedData = rows
      .map((row) => row[measureColumn])
      .filter((value) =>
        tuple ? typeof value === "string" : !Number.isNaN(value)
      );

    const clone = cloneData(context.params.state);
    const record = context.aggregations.data?.data?.at(0) || {};
    const subquery = context.params.modifiers.filterDegrouped({
      state: clone,
      record,
    });
    return [groupedData, subquery];
  }, [
    context.aggregations.data?.data,
    context.params.modifiers,
    context.params.state,
    measureColumn,
    tuple,
  ]);

  const [entityData, entityUuid] = useMemo(() => {
    const rows = context.entitiesList.data?.data || [];
    const measureColumn = context.params.selectors.getColumnForAggregation(
      context.params.state,
      context.params.state.aggregations?.at(0)
    );
    const entityData = rows
      .map((row) => Number(row.attributes[measureColumn]))
      .filter((value) => !Number.isNaN(value));
    const entityUuid = rows.map((row) => row.identifier).at(0);
    return [entityData, entityUuid];
  }, [
    context.entitiesList.data?.data,
    context.params.selectors,
    context.params.state,
  ]);

  const data = hasGroupsBys ? groupedData : entityData;
  const numberValue =
    Array.isArray(data) && data.length > 0 ? data[0] : tuple ? "0,0" : 0;
  const [fromVal, toVal] = `0,${numberValue}`.split(",").slice(-2);

  const fromValue = Number(valueToNumber(fromVal));
  const toValue = Number(valueToNumber(toVal));

  const value = tuple
    ? showChangeAs === "percentage"
      ? Math.round(((toValue - fromValue) / fromValue) * 100)
      : toValue - fromValue
    : toValue;

  const isPercent = tuple && showChangeAs === "percentage";
  let label =
    Number.isNaN(value) || !Number.isFinite(value)
      ? "n/a"
      : (tuple && value >= 0 ? "+" : "") +
        // Only divide by 100 for percentage types to display them correctly
        ValueFormatter.format(isPercent ? value / 100 : value, {
          type: type as ValueType,
          unit,
          precision,
        });
  // time comparisons return absolute time diffs, so prepend - if needed
  if (value < 0 && !label.startsWith("-")) label = `-${label}`;
  if (data.length === 0) label = "-";

  const compareValues = tuple
    ? ([
        formatValue(fromValue, type, unit, precision),
        formatValue(toValue, type, unit, precision),
      ] as [string, string])
    : undefined;
  return {
    value,
    label: unit ? label.split(" ").slice(0, -1).join(" ") : label,
    unit: unit ? label.split(" ").at(-1) : unit,
    ready,
    entityUuid,
    subquery,
    compareValues,
  };
}

export function MetricControls(props: { context: ExploreContext }) {
  const { view, params } = props.context;
  const metricColumns = (params.state.aggregations || []).map(
    (aggregation) => ({
      label: columnForAggregation(aggregation),
      value: columnForAggregation(aggregation),
    })
  );
  if (params.state.formulas?.length)
    metricColumns.push(
      ...params.state.formulas.map((formula) => ({
        label: formula.name,
        value: formula.name,
      }))
    );
  const reducer = params.state.aggregations?.at(0)?.reducer || "";
  const diffMethod = params.state.compareWith?.method || "";
  const config = useConfig(view.config);
  const disabled = !reducer;
  const changesDisabled = diffMethod !== "tuple";
  const hasTime = hasTimeUnit(props.context.aggregations);

  return (
    <div className="section">
      <div className="section-header">Display</div>
      <Flex start direction="column">
        <Flex spaceBetween style={{ width: "100%" }}>
          <div className={["viz-label", disabled ? "disabled" : ""].join(" ")}>
            Sort {reducer}
          </div>
          <Select
            disabled={disabled}
            bordered={false}
            className={["viz-select", disabled ? "disabled" : ""].join(" ")}
            value={config.sort}
            popupMatchSelectWidth={false}
            onChange={(direction) =>
              view.setConfig({ ...config, sort: direction })
            }
            options={[
              {
                value: "ascending",
                label: <span className="viz-select-option">Ascending</span>,
              },
              {
                value: "descending",
                label: <span className="viz-select-option">Descending</span>,
              },
            ]}
          />
        </Flex>
        <Flex spaceBetween style={{ marginTop: 8, width: "100%" }}>
          <div className={["viz-label", disabled ? "disabled" : ""].join(" ")}>
            Font size
          </div>
          <Select
            disabled={disabled}
            bordered={false}
            className={["viz-select", disabled ? "disabled" : ""].join(" ")}
            value={config.size}
            popupMatchSelectWidth={false}
            onChange={(size) => view.setConfig({ ...config, size })}
            options={[
              {
                value: "large",
                label: <span className="viz-select-option">Large</span>,
              },
              {
                value: "medium",
                label: <span className="viz-select-option">Medium</span>,
              },
              {
                value: "small",
                label: <span className="viz-select-option">Small</span>,
              },
            ]}
          />
        </Flex>
        <div style={{ width: "100%" }}>
          <CompareWithControls
            disabled={changesDisabled}
            changeColor={config.changeColor}
            showChangeAs={config.showChangeAs}
            showChangeValues={config.showChangeValues}
            onChangeColor={(changeColor) =>
              view.setConfig({ ...config, changeColor })
            }
            onChangeShowChangeValues={(showChangeValues) =>
              view.setConfig({ ...config, showChangeValues })
            }
            onChangeShowChangeAs={(showChangeAs) =>
              view.setConfig({ ...config, showChangeAs })
            }
          />
        </div>
        {hasTime && (
          <div style={{ width: "100%" }}>
            <FormattingControls
              disabled={!hasTime}
              timeUnit={config.timeUnit}
              onChangeTimeUnit={(timeUnit) =>
                view.setConfig({ ...config, timeUnit })
              }
            />
          </div>
        )}
        {metricColumns.length > 1 && (
          <div style={{ width: "100%" }}>
            <div
              style={{
                fontWeight: 600,
                fontSize: 11,
                opacity: disabled ? 0.5 : 1,
              }}
            >
              Metric Column
            </div>
            <div>
              <Select
                size="small"
                popupMatchSelectWidth={false}
                style={{ display: "block", width: "100%" }}
                defaultValue={metricColumns[0].value}
                options={metricColumns}
                onChange={(column) => view.setConfig({ ...config, column })}
              />
            </div>
          </div>
        )}
      </Flex>
    </div>
  );
}
